/**
 * Redux state for Select Entities modal (users/groups picker).
 * Caches fetched users and groups so that opening "Select owners" and "Select members"
 * reuses the same data and avoids re-fetches.
 */

const actionTypes = {
  SET_USERS: 'selectEntities/SET_USERS',
  SET_GROUPS: 'selectEntities/SET_GROUPS',
  SET_USERS_LOADING: 'selectEntities/SET_USERS_LOADING',
  SET_GROUPS_LOADING: 'selectEntities/SET_GROUPS_LOADING',
  SET_ERROR: 'selectEntities/SET_ERROR',
  CLEAR_CACHE: 'selectEntities/CLEAR_CACHE',
}

const initialState = {
  /** Key: cacheKey (customerId-organizationId-integrationType), value: { users: [], fetchedAt: number } */
  usersCache: {},
  /** Key: customerId, value: { groups: [], fetchedAt: number } */
  groupsCache: {},
  usersLoading: false,
  groupsLoading: false,
  error: null,
}

export const reducer = (state = initialState, action) => {
  const safeState = state && typeof state === 'object' && state !== null ? state : initialState
  switch (action.type) {
    case actionTypes.SET_USERS: {
      const { cacheKey, users } = action.payload
      return {
        ...safeState,
        usersCache: {
          ...safeState.usersCache,
          [cacheKey]: {
            users: users || [],
            fetchedAt: Date.now(),
          },
        },
        usersLoading: false,
        error: null,
      }
    }
    case actionTypes.SET_GROUPS: {
      const { cacheKey, groups } = action.payload
      const key = String(cacheKey)
      return {
        ...safeState,
        groupsCache: {
          ...safeState.groupsCache,
          [key]: {
            groups: groups || [],
            fetchedAt: Date.now(),
          },
        },
        groupsLoading: false,
        error: null,
      }
    }
    case actionTypes.SET_USERS_LOADING:
      return { ...safeState, usersLoading: action.payload }
    case actionTypes.SET_GROUPS_LOADING:
      return { ...safeState, groupsLoading: action.payload }
    case actionTypes.SET_ERROR:
      return { ...safeState, usersLoading: false, groupsLoading: false, error: action.payload }
    case actionTypes.CLEAR_CACHE:
      return { ...initialState }
    default:
      return safeState
  }
}

export const actions = {
  setUsers: (cacheKey, users) => ({
    type: actionTypes.SET_USERS,
    payload: { cacheKey, users },
  }),
  setGroups: (cacheKey, groups) => ({
    type: actionTypes.SET_GROUPS,
    payload: { cacheKey, groups },
  }),
  setUsersLoading: (loading) => ({
    type: actionTypes.SET_USERS_LOADING,
    payload: loading,
  }),
  setGroupsLoading: (loading) => ({
    type: actionTypes.SET_GROUPS_LOADING,
    payload: loading,
  }),
  setError: (error) => ({
    type: actionTypes.SET_ERROR,
    payload: error,
  }),
  clearCache: () => ({ type: actionTypes.CLEAR_CACHE }),
}

/** Build cache key for users list (same list used for owners and members). */
export const getUsersCacheKey = (customerId, organizationId, integrationType) => {
  if (customerId == null || organizationId == null || !integrationType) return null
  return `${customerId}-${organizationId}-${integrationType}`
}

/**
 * Build cache key for groups list. When allowedGroupTypes is provided (e.g. [SECURITY] for Entra member picker),
 * cache is keyed by customerId + types so different use cases don't share the same list.
 */
export const getGroupsCacheKey = (customerId, allowedGroupTypes) => {
  if (customerId == null) return null
  if (!allowedGroupTypes || allowedGroupTypes.length === 0) {
    return String(customerId)
  }
  return `${customerId}_${[...allowedGroupTypes].sort().join(',')}`
}
